"use client";

import { useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import { DZTypes } from "@dealerzone/libs";

interface PaginationProps {
  meta: DZTypes.Meta;
}

export default function Pagination({ meta }: PaginationProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const buildPageUrl = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (page === 1) {
      params.delete("page");
    } else {
      params.set("page", String(page));
    }
    const queryString = params.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  };

  const currentPage = meta.current_page;
  const lastPage = meta.last_page;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const delta = 2;

    if (lastPage <= 7) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > delta + 2) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - delta);
      const end = Math.min(lastPage - 1, currentPage + delta);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < lastPage - delta - 1) {
        pages.push("...");
      }

      pages.push(lastPage);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav className="mt-8 flex items-center justify-center gap-1.5" aria-label="Pagination">
      {currentPage > 1 && (
        <Link
          href={buildPageUrl(currentPage - 1)}
          className="px-4 py-2.5 text-zinc-600 hover:text-red-600 hover:bg-zinc-100 rounded-lg transition-colors"
        >
          <i className="bootstrap-chevron-left"></i>
          <span className="sr-only">Previous</span>
        </Link>
      )}

      {pageNumbers.map((page, index) => {
        if (page === "...") {
          return (
            <span key={`ellipsis-${index}`} className="px-4 py-2.5 text-zinc-400">
              ...
            </span>
          );
        }

        const pageNum = page as number;
        const isActive = pageNum === currentPage;

        return (
          <Link
            key={pageNum}
            href={buildPageUrl(pageNum)}
            className={`px-4 py-2.5 rounded-lg transition-colors ${
              isActive
                ? "bg-red-600 text-white font-medium"
                : "text-zinc-600 hover:text-red-600 hover:bg-zinc-100"
            }`}
            aria-current={isActive ? "page" : undefined}
          >
            {pageNum}
          </Link>
        );
      })}

      {currentPage < lastPage && (
        <Link
          href={buildPageUrl(currentPage + 1)}
          className="px-4 py-2.5 text-zinc-600 hover:text-red-600 hover:bg-zinc-100 rounded-lg transition-colors"
        >
          Next
          <i className="bootstrap-chevron-right ml-1"></i>
        </Link>
      )}
    </nav>
  );
}
